import React, { useState, useRef } from "react";
import {
  Modal,
  Button,
  Stack,
  Text,
  Textarea,
  Badge,
  Paper,
  Group,
  Box,
  Alert,
  UnstyledButton,
  Anchor,
} from "@mantine/core";
import {
  IconVideo,
  IconUpload,
  IconUserCheck,
  IconShieldCheck,
  IconMessage,
  IconCheck,
  IconMicrophone,
  IconClipboardList,
  IconPhoto,
  IconX,
  IconQuestionMark,
  IconAlertCircle,
  IconChevronLeft,
  IconChevronRight,
  IconRefresh,
  IconCircleCheck,
  IconCircleX,
} from "@tabler/icons-react";
import RecordPitchModal from "../Video/RecordPitchModal";
import "../Training/QuizBuilder.css";

const hasDisplayValue = value => {
  if (value === null || value === undefined) return false;
  if (value === 0 || value === "0") return false;
  if (typeof value === "string" && value.trim() === "") return false;
  return true;
};

function ProgramTaskModal({
  isOpen,
  onClose,
  task,
  taskNumber,
  onComplete,
  onTextSubmit,
  onQuizSubmit,
  onNavigateToVideo,
  isSubmitting = false,
  completedDetails = null,
  formatDate,
  quizError: externalQuizError = null,
  inline = false,
}) {
  const [textResponse, setTextResponse] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  // Multi-question carousel state
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [multiAnswers, setMultiAnswers] = useState([]);
  const [quizError, setQuizError] = useState(null);
  const [quizCorrect, setQuizCorrect] = useState(false);
  const [quizResult, setQuizResult] = useState(null); // { score, passing_score, passed }
  const [taskResult, setTaskResult] = useState(null); // { success, message, error } for non-quiz tasks
  const [showPitchModal, setShowPitchModal] = useState(false);
  const [pitchChannelId, setPitchChannelId] = useState(null);
  const [selectedPitchVideo, setSelectedPitchVideo] = useState(null);
  const [uploadingPitch, setUploadingPitch] = useState(false);
  const fileInputRef = useRef(null);
  const uploadFileInputRef = useRef(null);
  const pitchVideoInputRef = useRef(null);
  const videoRef = useRef(null);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);

  // Determine if this is a multi-question quiz
  const isMultiQuiz =
    Array.isArray(task?.quiz_questions) && task.quiz_questions.length > 0;

  // Initialize multiAnswers when task changes
  React.useEffect(() => {
    if (isMultiQuiz && task?.quiz_questions) {
      setMultiAnswers(new Array(task.quiz_questions.length).fill(null));
      setCurrentQuestionIdx(0);
    }
  }, [task?.id, isMultiQuiz]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setTextResponse("");
    setImageFile(null);
    setImagePreview(null);
    setSelectedAnswer(null);
    setMultiAnswers([]);
    setCurrentQuestionIdx(0);
    setQuizError(null);
    setQuizCorrect(false);
    setQuizResult(null);
    setTaskResult(null);
    setShowPitchModal(false);
    setPitchChannelId(null);
    setSelectedPitchVideo(null);
    setUploadingPitch(false);
    onClose();
  };

  const handleImageSelect = e => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleComplete = async () => {
    try {
      await onComplete(imageFile || null);
      setTaskResult({
        success: true,
        message: "Task Completed!",
        subtitle: task.requires_admin_approval
          ? "Your manager will review this task"
          : "Great job completing this task!",
      });
    } catch (err) {
      setTaskResult({
        success: false,
        message: "Failed to Complete Task",
        error:
          err?.message ||
          err?.response?.data?.message ||
          "An error occurred. Please try again.",
      });
    }
  };

  const handleSubmitResponse = async () => {
    if (textResponse.trim()) {
      try {
        await onTextSubmit(textResponse, imageFile || null);
        setTaskResult({
          success: true,
          message: "Response Submitted!",
          subtitle: task.requires_admin_approval
            ? "Your manager will review your response"
            : "Your response has been recorded",
        });
      } catch (err) {
        setTaskResult({
          success: false,
          message: "Failed to Submit Response",
          error:
            err?.message ||
            err?.response?.data?.message ||
            "An error occurred. Please try again.",
        });
      }
    }
  };

  const handleRetry = () => {
    setTaskResult(null);
    setQuizResult(null);
    setQuizError(null);
    setTextResponse("");
    setImageFile(null);
    setImagePreview(null);
    setSelectedAnswer(null);
    setMultiAnswers(
      isMultiQuiz ? new Array(task.quiz_questions.length).fill(null) : []
    );
    setCurrentQuestionIdx(0);
  };

  if (!task) return null;

  const requiresImage = !!task.requires_image;
  const requiresAdminApproval = !!task.requires_admin_approval;

  const getTaskTypeIcon = taskType => {
    switch (taskType) {
      case "VIDEO":
        return <IconVideo size={20} />;
      case "UPLOAD":
        return <IconUpload size={20} />;
      case "USER_APPROVE":
        return <IconUserCheck size={20} />;
      case "ADMIN_APPROVE":
        return <IconShieldCheck size={20} />;
      case "TEXT_RESPONSE":
        return <IconMessage size={20} />;
      case "PITCH_REVIEW":
        return <IconMicrophone size={20} />;
      case "QUIZ":
        return <IconQuestionMark size={20} />;
      default:
        return <IconClipboardList size={20} />;
    }
  };

  const getTaskTypeBadgeColor = taskType => {
    switch (taskType) {
      case "VIDEO":
        return "blue";
      case "UPLOAD":
        return "violet";
      case "USER_APPROVE":
        return "cyan";
      case "ADMIN_APPROVE":
        return "orange";
      case "TEXT_RESPONSE":
        return "grape";
      case "PITCH_REVIEW":
        return "pink";
      case "QUIZ":
        return "teal";
      default:
        return "gray";
    }
  };

  const getTaskTypeLabel = taskType => {
    switch (taskType) {
      case "VIDEO":
        return "Watch Video";
      case "UPLOAD":
        return "Upload File";
      case "USER_APPROVE":
        return "Self-Complete";
      case "ADMIN_APPROVE":
        return "Admin Approval";
      case "TEXT_RESPONSE":
        return "Text Response";
      case "PITCH_REVIEW":
        return "Pitch Review";
      case "QUIZ":
        return "Quiz";
      default:
        return taskType;
    }
  };

  const handleQuizSubmit = () => {
    setQuizError(null);
    setQuizResult(null);

    if (isMultiQuiz) {
      // Check all answered
      if (multiAnswers.some(a => a === null)) {
        const firstUnanswered = multiAnswers.findIndex(a => a === null);
        setCurrentQuestionIdx(firstUnanswered);
        setQuizError("Please answer all questions before submitting.");
        return;
      }
      if (onQuizSubmit) {
        onQuizSubmit(multiAnswers, setQuizError)
          .then(() => {
            // Calculate score for passed quiz
            const totalQuestions = task.quiz_questions.length;
            const correctAnswers = multiAnswers.filter(
              (answer, idx) =>
                answer === task.quiz_questions[idx].correct_answer_index
            ).length;
            const score = Math.round((correctAnswers / totalQuestions) * 100);
            setQuizResult({
              score: score,
              passing_score: task.quiz_passing_score || 75,
              passed: true,
            });
          })
          .catch(err => {
            const responseData = err?.response?.data;
            if (typeof responseData === "string") {
              try {
                const parsed = JSON.parse(responseData);
                if (parsed.score !== undefined) {
                  setQuizResult({
                    score: parsed.score,
                    passing_score: parsed.passing_score,
                    passed: false,
                  });
                  return;
                }
              } catch (e) {
                // Parsing failed, continue to fallback
              }
            }

            // Fallback: show generic error
            setQuizError(
              err?.message || "Failed to submit quiz. Please try again."
            );
          });
      }
    } else {
      // Legacy single-question
      if (selectedAnswer === null) return;
      if (onQuizSubmit) {
        onQuizSubmit(selectedAnswer, setQuizError)
          .then(() => {
            setQuizCorrect(true);
            setTimeout(() => handleClose(), 1800);
          })
          .catch(() => {});
      }
    }
  };

  // A rejected completion is NOT "done" — the rep still needs to resubmit
  const isCompleted =
    completedDetails !== null && !completedDetails?.is_rejected;

  // Generic Task Results Screen (for non-quiz tasks)
  const TaskResultsScreen = () => {
    if (!taskResult) return null;

    const isSuccess = taskResult.success;
    return (
      <Stack gap="lg" align="center" py="xl">
        {/* Icon - green checkmark for success, red X for failure */}
        <Box
          style={{
            width: 96,
            height: 96,
            borderRadius: "50%",
            background: isSuccess ? "#16a34a" : "#ef4444",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSuccess ? (
            <IconCircleCheck size={56} color="white" stroke={2.5} />
          ) : (
            <IconCircleX size={56} color="white" stroke={2.5} />
          )}
        </Box>

        {/* Message */}
        <Stack gap="xs" align="center">
          <Text
            style={{
              fontSize: "2rem",
              fontWeight: 800,
              color: isSuccess ? "#16a34a" : "#ef4444",
              letterSpacing: "-0.5px",
            }}
          >
            {taskResult.message}
          </Text>
          {taskResult.subtitle && (
            <Text size="lg" fw={600} c="dimmed" ta="center">
              {taskResult.subtitle}
            </Text>
          )}
          {taskResult.error && (
            <Text size="md" c="red" ta="center" style={{ maxWidth: 400 }}>
              {taskResult.error}
            </Text>
          )}
        </Stack>

        {/* Action buttons */}
        {isSuccess ? (
          <Button
            fullWidth
            size="lg"
            onClick={handleClose}
            leftSection={<IconX size={18} />}
          >
            Close
          </Button>
        ) : (
          <Group gap="sm" style={{ width: "100%" }}>
            <Button
              flex={1}
              size="lg"
              variant="outline"
              onClick={handleClose}
              leftSection={<IconX size={18} />}
            >
              Close
            </Button>
            <Button
              flex={1}
              size="lg"
              onClick={handleRetry}
              leftSection={<IconRefresh size={18} />}
              color="violet"
            >
              Try Again
            </Button>
          </Group>
        )}
      </Stack>
    );
  };

  // Image upload UI (shared between USER_APPROVE and TEXT_RESPONSE)
  const ImageUploadSection = () => (
    <Box>
      <Text size="sm" fw={600} mb="xs">
        {requiresImage ? (
          <>
            Photo Required{" "}
            <Text span c="red" fw={700}>
              *
            </Text>
          </>
        ) : (
          "Photo (optional)"
        )}
      </Text>
      {imagePreview ? (
        <Box style={{ position: "relative", display: "inline-block" }}>
          <img
            src={imagePreview}
            alt="Selected"
            style={{
              width: "100%",
              maxHeight: 240,
              objectFit: "contain",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              display: "block",
            }}
          />
          <button
            onClick={handleRemoveImage}
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              background: "rgba(0,0,0,0.6)",
              border: "none",
              borderRadius: "50%",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "white",
            }}
          >
            <IconX size={14} />
          </button>
        </Box>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            width: "100%",
            padding: "20px",
            border: "2px dashed #d1d5db",
            borderRadius: 8,
            background: "#f9fafb",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            color: "#6b7280",
          }}
        >
          <IconPhoto size={28} stroke={1.5} />
          <Text size="sm" fw={500}>
            Tap to upload a photo
          </Text>
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageSelect}
      />
    </Box>
  );

  // ── Multi-question carousel ──────────────────────────────────────────────
  const MultiQuizCarousel = () => {
    const questions = task.quiz_questions;
    const total = questions.length;
    const q = questions[currentQuestionIdx];
    const answeredCount = multiAnswers.filter(a => a !== null).length;
    const allAnswered = answeredCount === total;
    const progressPercent = (answeredCount / total) * 100;

    // Show results screen if quiz was submitted (passed or failed)
    if (quizResult) {
      const isPassed = quizResult.passed;
      const correctCount = Math.round((quizResult.score / 100) * total);
      const incorrectCount = total - correctCount;

      return (
        <Stack gap="xl" align="center" py="xl">
          {/* Orange circle with score */}
          <Box
            style={{
              width: 140,
              height: 140,
              borderRadius: "50%",
              background: "#ff6b35",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              style={{
                fontSize: "3rem",
                fontWeight: 800,
                color: "white",
                lineHeight: 1,
              }}
            >
              {quizResult.score}%
            </Text>
          </Box>

          {/* Status message */}
          <Stack gap="xs" align="center">
            <Group gap="xs">
              <IconCircleX size={24} color="#ff6b35" />
              <Text
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                }}
              >
                {isPassed ? "Great Job!" : "Not Quite There"}
              </Text>
            </Group>
            <Text size="md" c="dimmed" ta="center">
              {isPassed
                ? `You passed! (Required: ${quizResult.passing_score}%)`
                : `You need ${quizResult.passing_score}% to pass. Keep trying!`}
            </Text>
          </Stack>

          {/* Stats breakdown */}
          <Group gap="xl" style={{ width: "100%" }} justify="center">
            <Stack gap={4} align="center">
              <Text
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                }}
              >
                {correctCount}
              </Text>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Correct
              </Text>
            </Stack>
            <Stack gap={4} align="center">
              <Text
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                }}
              >
                {incorrectCount}
              </Text>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Incorrect
              </Text>
            </Stack>
            <Stack gap={4} align="center">
              <Text
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                }}
              >
                {quizResult.passing_score}%
              </Text>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Required
              </Text>
            </Stack>
          </Group>

          {/* Cooldown message - only show for failed */}
          {task.quiz_cooldown_minutes > 0 && !isPassed && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="orange"
              variant="light"
              style={{ width: "100%" }}
            >
              Wait {task.quiz_cooldown_minutes} minute
              {task.quiz_cooldown_minutes !== 1 ? "s" : ""} before retrying
            </Alert>
          )}

          {/* Action buttons */}
          <Stack gap="sm" style={{ width: "100%" }}>
            {!isPassed && (
              <Button
                fullWidth
                size="lg"
                onClick={() => {
                  setQuizResult(null);
                  setMultiAnswers(new Array(total).fill(null));
                  setCurrentQuestionIdx(0);
                  setQuizError(null);
                }}
                leftSection={<IconRefresh size={18} />}
                disabled={task.quiz_cooldown_minutes > 0}
                styles={{
                  root: {
                    backgroundColor: "#ff6b35",
                    "&:hover": {
                      backgroundColor: "#e55a2b",
                    },
                  },
                }}
              >
                Try Again
              </Button>
            )}
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack gap="lg">
        {/* Progress bar and meta info */}
        <Box>
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed" fw={500}>
              Progress: {answeredCount}/{total} answered
            </Text>
            <Text size="sm" c="dimmed" fw={500}>
              Pass: {task.quiz_passing_score || 75}%
            </Text>
          </Group>
          <Box
            style={{
              width: "100%",
              height: 8,
              background: "#e5e7eb",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <Box
              style={{
                width: `${progressPercent}%`,
                height: "100%",
                background: "#3b82f6",
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>

        {/* Question header */}
        <Text size="sm" c="dimmed" fw={600} tt="uppercase">
          QUESTION {currentQuestionIdx + 1} OF {total}
        </Text>

        {/* Question text */}
        <Text size="xl" fw={700} style={{ lineHeight: 1.4 }}>
          {q.question}
        </Text>

        {/* Answer options */}
        <Stack gap="sm">
          {q.answers.map((answer, aIdx) => {
            const isSelected = multiAnswers[currentQuestionIdx] === aIdx;
            return (
              <UnstyledButton
                key={aIdx}
                onClick={() => {
                  const newAnswers = [...multiAnswers];
                  newAnswers[currentQuestionIdx] = aIdx;
                  setMultiAnswers(newAnswers);
                  setQuizError(null);
                }}
                style={{
                  width: "100%",
                  padding: "16px 20px",
                  borderRadius: 8,
                  border: isSelected
                    ? "2px solid #3b82f6"
                    : "2px solid #e5e7eb",
                  background: isSelected ? "#eff6ff" : "#fff",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: isSelected
                      ? "6px solid #3b82f6"
                      : "2px solid #d1d5db",
                    background: "#fff",
                    flexShrink: 0,
                  }}
                />
                <Text size="md" fw={isSelected ? 600 : 400} c="dark">
                  {answer}
                </Text>
              </UnstyledButton>
            );
          })}
        </Stack>

        {/* Question navigation circles */}
        <Group justify="center" gap="xs" mt="md">
          <button
            type="button"
            onClick={() => setCurrentQuestionIdx(i => Math.max(0, i - 1))}
            disabled={currentQuestionIdx === 0}
            style={{
              background: "none",
              border: "none",
              cursor: currentQuestionIdx === 0 ? "not-allowed" : "pointer",
              opacity: currentQuestionIdx === 0 ? 0.3 : 1,
              padding: 4,
            }}
          >
            <IconChevronLeft size={20} />
          </button>
          {questions.map((_, idx) => {
            const isAnswered = multiAnswers[idx] !== null;
            const isCurrent = idx === currentQuestionIdx;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setCurrentQuestionIdx(idx)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "none",
                  background: isCurrent
                    ? "#3b82f6"
                    : isAnswered
                      ? "#22c55e"
                      : "#e5e7eb",
                  color: isCurrent || isAnswered ? "#fff" : "#6b7280",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                {idx + 1}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() =>
              setCurrentQuestionIdx(i => Math.min(total - 1, i + 1))
            }
            disabled={currentQuestionIdx === total - 1}
            style={{
              background: "none",
              border: "none",
              cursor:
                currentQuestionIdx === total - 1 ? "not-allowed" : "pointer",
              opacity: currentQuestionIdx === total - 1 ? 0.3 : 1,
              padding: 4,
            }}
          >
            <IconChevronRight size={20} />
          </button>
        </Group>

        {/* Error message */}
        {(quizError || externalQuizError) && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {quizError || externalQuizError}
          </Alert>
        )}

        {/* Bottom navigation bar */}
        <Group justify="space-between" mt="md" gap="md">
          <Button
            size="lg"
            variant="default"
            onClick={handleClose}
            styles={{
              root: {
                border: "1px solid #e5e7eb",
                color: "#374151",
                backgroundColor: "#fff",
                borderRadius: "9999px",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                },
              },
            }}
          >
            Save & Exit
          </Button>
          {currentQuestionIdx < total - 1 ? (
            <Button
              size="lg"
              onClick={() => setCurrentQuestionIdx(i => i + 1)}
              rightSection={<IconChevronRight size={18} />}
              styles={{
                root: {
                  borderRadius: "9999px",
                },
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              size="lg"
              loading={isSubmitting}
              disabled={!allAnswered}
              onClick={handleQuizSubmit}
              rightSection={<IconChevronRight size={18} />}
              styles={{
                root: {
                  borderRadius: "9999px",
                },
              }}
            >
              Submit Quiz
            </Button>
          )}
        </Group>
      </Stack>
    );
  };

  // ── inline render (accordion panel) ─────────────────────────────────────
  if (inline) {
    return (
      <div
        style={{
          borderTop: "1px solid #e5e7eb",
          background: "#ffffff",
          borderRadius: "0 0 10px 10px",
          padding: "20px 20px 16px",
        }}
      >
        {/* Correct answer full-screen overlay for single quiz */}
        {quizCorrect && (
          <Box
            style={{
              background: "#ffffff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              padding: "40px 0",
            }}
          >
            <Box
              style={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                background: "#16a34a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheck size={56} color="white" stroke={3} />
            </Box>
            <Text
              style={{
                fontSize: "1.75rem",
                fontWeight: 800,
                color: "#16a34a",
                letterSpacing: "-0.5px",
              }}
            >
              {isMultiQuiz ? "Quiz Passed!" : "Correct Answer!"}
            </Text>
          </Box>
        )}

        <Stack gap="lg">
          {/* Task Description */}
          {(task.description || task.task_desc) && (
            <Paper p="md" radius="sm" withBorder>
              <Text size="sm" fw={600} mb="xs" c="dimmed">
                Description
              </Text>
              <Text size="sm">{task.description || task.task_desc}</Text>
            </Paper>
          )}

          {/* File Attachment from manager */}
          {hasDisplayValue(task.file_url) &&
            hasDisplayValue(task.file_name) && (
              <Box
                p="sm"
                style={{
                  background: "#f3f0ff",
                  borderRadius: 8,
                  border: "1px solid #c5b3f5",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <IconUpload size={18} color="#7c3aed" />
                <Anchor
                  href={task.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  c="violet.8"
                  fw={500}
                  style={{ flex: 1 }}
                  lineClamp={1}
                >
                  {task.file_name}
                </Anchor>
              </Box>
            )}

          {/* Manager feedback banner for rejected tasks */}
          {completedDetails?.is_rejected &&
            completedDetails?.manager_feedback && (
              <Box
                p="sm"
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fca5a5",
                  borderRadius: 8,
                }}
              >
                <Text size="xs" fw={700} c="red" mb={4}>
                  Manager Feedback
                </Text>
                <Text size="sm" c="dark">
                  {completedDetails.manager_feedback}
                </Text>
              </Box>
            )}

          {/* Completion Info */}
          {isCompleted && (
            <Paper p="md" radius="sm" withBorder bg="green.0">
              <Stack gap="md">
                {completedDetails.completed_at && (
                  <Group gap="xs">
                    <IconCheck size={16} color="var(--mantine-color-green-6)" />
                    <Text size="sm" c="green" fw={600}>
                      Completed{" "}
                      {formatDate && formatDate(completedDetails.completed_at)}
                    </Text>
                  </Group>
                )}
                {completedDetails.text_response && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">
                      Your Response:
                    </Text>
                    <Paper p="sm" radius="sm" bg="white" withBorder>
                      <Text size="sm">{completedDetails.text_response}</Text>
                    </Paper>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Show results screen for non-quiz tasks */}
          {taskResult && <TaskResultsScreen />}

          {/* Action Area for Incomplete Tasks */}
          {!isCompleted && !taskResult && (
            <>
              {task.task_type === "USER_APPROVE" && (
                <Stack gap="md">
                  {requiresImage && <ImageUploadSection />}
                  {requiresAdminApproval && (
                    <Paper p="sm" radius="sm" withBorder bg="orange.0">
                      <Group gap="xs">
                        <IconShieldCheck
                          size={16}
                          color="var(--mantine-color-orange-6)"
                        />
                        <Text size="sm" c="orange" fw={500}>
                          After completing, your manager will need to approve
                          this task.
                        </Text>
                      </Group>
                    </Paper>
                  )}
                  <Button
                    fullWidth
                    size="lg"
                    loading={isSubmitting}
                    disabled={requiresImage && !imageFile}
                    onClick={handleComplete}
                    leftSection={<IconCheck size={20} />}
                    style={{ background: "#ff6b35", border: "none" }}
                  >
                    {requiresImage && !imageFile
                      ? "Upload a photo to continue"
                      : "Mark as Completed"}
                  </Button>
                </Stack>
              )}

              {task.task_type === "TEXT_RESPONSE" && (
                <Stack gap="md">
                  <Textarea
                    placeholder="Enter your response..."
                    value={textResponse}
                    onChange={e => setTextResponse(e.target.value)}
                    minRows={4}
                    autosize
                    label="Your Response"
                  />
                  <ImageUploadSection />
                  {requiresAdminApproval && (
                    <Paper p="sm" radius="sm" withBorder bg="orange.0">
                      <Group gap="xs">
                        <IconShieldCheck
                          size={16}
                          color="var(--mantine-color-orange-6)"
                        />
                        <Text size="sm" c="orange" fw={500}>
                          Your response will be reviewed by your manager before
                          this task is marked complete.
                        </Text>
                      </Group>
                    </Paper>
                  )}
                  <Button
                    fullWidth
                    size="lg"
                    loading={isSubmitting}
                    disabled={
                      !textResponse.trim() || (requiresImage && !imageFile)
                    }
                    onClick={handleSubmitResponse}
                    leftSection={<IconCheck size={20} />}
                    style={{ background: "#ff6b35", border: "none" }}
                  >
                    Submit Response
                  </Button>
                </Stack>
              )}

              {task.task_type === "VIDEO" && task.training_video && (
                <Stack gap="md">
                  {hasDisplayValue(task.training_video.video_url) && (
                    <Box
                      style={{
                        width: "100%",
                        borderRadius: 8,
                        overflow: "hidden",
                        background: "#000",
                      }}
                    >
                      <video
                        ref={videoRef}
                        controls
                        controlsList="nodownload"
                        style={{
                          width: "100%",
                          maxHeight: "400px",
                          display: "block",
                        }}
                        src={task.training_video.video_url}
                        onTimeUpdate={e => {
                          const currentTime = e.target.currentTime;
                          if (currentTime > maxWatchedTime) {
                            setMaxWatchedTime(currentTime);
                          }
                        }}
                        onSeeking={e => {
                          const currentTime = e.target.currentTime;
                          if (currentTime > maxWatchedTime + 0.5) {
                            e.target.currentTime = maxWatchedTime;
                          }
                        }}
                        onEnded={async () => {
                          await handleComplete();
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </Box>
                  )}
                  <Paper p="sm" radius="sm" withBorder bg="blue.0">
                    <Text size="sm" c="blue" fw={500} ta="center">
                      Watch the video to completion to mark this task as done
                    </Text>
                  </Paper>
                </Stack>
              )}

              {task.task_type === "PITCH_REVIEW" && (
                <Stack gap="md">
                  <Paper p="md" radius="sm" withBorder bg="pink.0">
                    <Group gap="xs" mb="sm">
                      <IconMicrophone
                        size={16}
                        color="var(--mantine-color-pink-6)"
                      />
                      <Text size="sm" c="pink" fw={600}>
                        Pitch Review Required
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      Upload a pitch video for your manager to review. This task
                      will be marked complete after submission and requires
                      manager approval.
                    </Text>
                  </Paper>

                  {selectedPitchVideo && (
                    <Paper p="md" radius="sm" withBorder>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconVideo
                            size={20}
                            color="var(--mantine-color-pink-6)"
                          />
                          <div>
                            <Text size="sm" fw={600}>
                              {selectedPitchVideo.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {(
                                selectedPitchVideo.size /
                                (1024 * 1024)
                              ).toFixed(2)}{" "}
                              MB
                            </Text>
                          </div>
                        </Group>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={() => {
                            setSelectedPitchVideo(null);
                            if (pitchVideoInputRef.current)
                              pitchVideoInputRef.current.value = "";
                          }}
                        >
                          Remove
                        </Button>
                      </Group>
                    </Paper>
                  )}

                  <Group gap="sm">
                    <Button
                      flex={1}
                      size="lg"
                      variant="outline"
                      onClick={() => pitchVideoInputRef.current?.click()}
                      leftSection={<IconUpload size={20} />}
                      color="pink"
                      disabled={uploadingPitch}
                    >
                      {selectedPitchVideo ? "Change Video" : "Select Video"}
                    </Button>
                    <Button
                      flex={1}
                      size="lg"
                      onClick={() => setShowPitchModal(true)}
                      leftSection={<IconMicrophone size={20} />}
                      color="pink"
                      disabled={uploadingPitch}
                    >
                      Record Video
                    </Button>
                  </Group>

                  {selectedPitchVideo && (
                    <Button
                      fullWidth
                      size="lg"
                      loading={uploadingPitch}
                      onClick={async () => {
                        setUploadingPitch(true);
                        try {
                          await onComplete(null);
                          setTaskResult({
                            success: true,
                            message: "Pitch Submitted!",
                            subtitle:
                              "Your pitch has been submitted for manager review.",
                          });
                        } catch (err) {
                          setTaskResult({
                            success: false,
                            message: "Failed to Submit Pitch",
                            error:
                              err?.message ||
                              "An error occurred. Please try again.",
                          });
                        } finally {
                          setUploadingPitch(false);
                        }
                      }}
                      leftSection={<IconCheck size={20} />}
                      color="pink"
                    >
                      Submit Pitch
                    </Button>
                  )}

                  <input
                    ref={pitchVideoInputRef}
                    type="file"
                    accept="video/*"
                    style={{ display: "none" }}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) setSelectedPitchVideo(file);
                    }}
                  />
                </Stack>
              )}

              {task.task_type === "UPLOAD" && (
                <Stack gap="md">
                  <Paper p="md" radius="sm" withBorder bg="violet.0">
                    <Group gap="xs" mb="sm">
                      <IconUpload
                        size={16}
                        color="var(--mantine-color-violet-6)"
                      />
                      <Text size="sm" c="violet" fw={600}>
                        File Upload Required
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      Upload a file to complete this task.
                      {requiresAdminApproval &&
                        " Your manager will review your submission."}
                    </Text>
                  </Paper>

                  {uploadedFile ? (
                    <Paper p="md" radius="sm" withBorder>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconUpload
                            size={20}
                            color="var(--mantine-color-violet-6)"
                          />
                          <div>
                            <Text size="sm" fw={600}>
                              {uploadedFile.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {(uploadedFile.size / (1024 * 1024)).toFixed(2)}{" "}
                              MB
                            </Text>
                          </div>
                        </Group>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={() => {
                            setUploadedFile(null);
                            if (uploadFileInputRef.current)
                              uploadFileInputRef.current.value = "";
                          }}
                        >
                          Remove
                        </Button>
                      </Group>
                    </Paper>
                  ) : (
                    <button
                      onClick={() => uploadFileInputRef.current?.click()}
                      style={{
                        width: "100%",
                        padding: "40px 20px",
                        border: "2px dashed #d1d5db",
                        borderRadius: 8,
                        background: "#f9fafb",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 12,
                        color: "#6b7280",
                      }}
                    >
                      <IconUpload size={32} stroke={1.5} />
                      <Text size="md" fw={600}>
                        Click to upload a file
                      </Text>
                      <Text size="sm" c="dimmed">
                        Any file type accepted
                      </Text>
                    </button>
                  )}

                  {uploadedFile && (
                    <Button
                      fullWidth
                      size="lg"
                      loading={isSubmitting}
                      onClick={async () => {
                        try {
                          await onComplete(uploadedFile);
                          setTaskResult({
                            success: true,
                            message: "File Uploaded!",
                            subtitle: requiresAdminApproval
                              ? "Your manager will review your submission"
                              : "Your file has been uploaded successfully",
                          });
                        } catch (err) {
                          setTaskResult({
                            success: false,
                            message: "Failed to Upload File",
                            error:
                              err?.message ||
                              "An error occurred. Please try again.",
                          });
                        }
                      }}
                      leftSection={<IconCheck size={20} />}
                      style={{ background: "#ff6b35", border: "none" }}
                    >
                      Submit File
                    </Button>
                  )}

                  <input
                    ref={uploadFileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) setUploadedFile(file);
                    }}
                  />
                </Stack>
              )}

              {task.task_type === "ADMIN_APPROVE" && (
                <Paper p="md" radius="sm" withBorder>
                  <Text size="sm" c="dimmed" fs="italic" ta="center">
                    This task requires manager approval. Complete any required
                    actions and wait for your manager to review.
                  </Text>
                </Paper>
              )}

              {/* Multi-question quiz carousel */}
              {task.task_type === "QUIZ" && isMultiQuiz && (
                <MultiQuizCarousel />
              )}

              {/* Legacy single-question quiz */}
              {task.task_type === "QUIZ" &&
                !isMultiQuiz &&
                task.quiz_question && (
                  <Stack gap="md">
                    <Paper p="md" radius="sm" withBorder>
                      <Text size="sm" fw={600} mb="xs" c="dimmed">
                        Question
                      </Text>
                      <Text size="sm" fw={500}>
                        {task.quiz_question}
                      </Text>
                    </Paper>

                    {Array.isArray(task.quiz_answers) &&
                      task.quiz_answers.length > 0 && (
                        <Stack gap="xs">
                          <Text size="sm" fw={600}>
                            Select your answer:
                          </Text>
                          {task.quiz_answers.map((answer, idx) => {
                            const isSelected = selectedAnswer === idx;
                            return (
                              <UnstyledButton
                                key={idx}
                                onClick={() => {
                                  setSelectedAnswer(idx);
                                  setQuizError(null);
                                }}
                                style={{
                                  width: "100%",
                                  padding: "12px 16px",
                                  borderRadius: 8,
                                  border: isSelected
                                    ? "2px solid var(--mantine-color-teal-6)"
                                    : "2px solid #e5e7eb",
                                  background: isSelected
                                    ? "var(--mantine-color-teal-0)"
                                    : "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 12,
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                              >
                                <Box
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    border: isSelected
                                      ? "2px solid var(--mantine-color-teal-6)"
                                      : "2px solid #d1d5db",
                                    background: isSelected
                                      ? "var(--mantine-color-teal-6)"
                                      : "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  {isSelected && (
                                    <IconCheck size={14} color="white" />
                                  )}
                                </Box>
                                <Text
                                  size="sm"
                                  fw={isSelected ? 600 : 400}
                                  c={isSelected ? "teal" : "dark"}
                                >
                                  {answer}
                                </Text>
                              </UnstyledButton>
                            );
                          })}
                        </Stack>
                      )}

                    {(quizError || externalQuizError) && (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="orange"
                        variant="light"
                      >
                        {quizError || externalQuizError}
                      </Alert>
                    )}

                    <Button
                      fullWidth
                      size="lg"
                      loading={isSubmitting}
                      disabled={selectedAnswer === null}
                      onClick={handleQuizSubmit}
                      leftSection={<IconCheck size={20} />}
                      color="teal"
                    >
                      {selectedAnswer === null
                        ? "Select an answer to continue"
                        : "Submit Answer"}
                    </Button>
                  </Stack>
                )}
            </>
          )}
        </Stack>

        {/* Pitch Review Modal */}
        {showPitchModal && (
          <RecordPitchModal
            opened={showPitchModal}
            onClose={() => setShowPitchModal(false)}
            onSubmit={async result => {
              if (result?.success) {
                try {
                  await onComplete(null);
                  setTaskResult({
                    success: true,
                    message: "Pitch Submitted!",
                    subtitle:
                      "Your pitch has been submitted for manager review.",
                  });
                  setShowPitchModal(false);
                } catch (err) {
                  setTaskResult({
                    success: false,
                    message: "Failed to Complete Task",
                    error:
                      err?.message || "An error occurred. Please try again.",
                  });
                  setShowPitchModal(false);
                }
              }
            }}
            videoTitle={task.title || task.task_name || "Pitch Review"}
            videoId={null}
            courseId={null}
            initialChannelId={pitchChannelId}
            onSubmitSuccess={() => {}}
          />
        )}
      </div>
    );
  }

  // ── modal render (default) ────────────────────────────────────────────────
  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      size="lg"
      styles={{
        body: { position: "relative", overflow: "hidden" },
      }}
      title={
        <Group gap="md">
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isCompleted
                ? "var(--mantine-color-green-6)"
                : "var(--mantine-color-gray-2)",
              flexShrink: 0,
            }}
          >
            {isCompleted ? (
              <IconCheck size={20} color="white" />
            ) : (
              <Text size="md" fw={700} c="dimmed">
                {taskNumber}
              </Text>
            )}
          </Box>
          <Text size="lg" fw={600}>
            {task.title || task.task_name || `Task ${task.id}`}
          </Text>
        </Group>
      }
    >
      {/* Correct answer full-screen overlay */}
      {quizCorrect && (
        <Box
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            background: "#ffffff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            borderRadius: 8,
          }}
        >
          <Box
            style={{
              width: 96,
              height: 96,
              borderRadius: "50%",
              background: "#16a34a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconCheck size={56} color="white" stroke={3} />
          </Box>
          <Text
            style={{
              fontSize: "1.75rem",
              fontWeight: 800,
              color: "#16a34a",
              letterSpacing: "-0.5px",
            }}
          >
            {isMultiQuiz ? "Quiz Passed!" : "Correct Answer!"}
          </Text>
        </Box>
      )}

      <Stack gap="lg">
        {/* Task Type Badge */}
        <Group>
          <Badge
            color={getTaskTypeBadgeColor(task.task_type)}
            variant="light"
            leftSection={getTaskTypeIcon(task.task_type)}
            size="lg"
          >
            {getTaskTypeLabel(task.task_type)}
          </Badge>
          {requiresAdminApproval && (
            <Badge
              color="orange"
              variant="light"
              leftSection={<IconShieldCheck size={14} />}
              size="lg"
            >
              Admin Approval Required
            </Badge>
          )}
        </Group>

        {/* Task Description */}
        {(task.description || task.task_desc) && (
          <Paper p="md" radius="sm" withBorder>
            <Text size="sm" fw={600} mb="xs" c="dimmed">
              Description
            </Text>
            <Text size="sm">{task.description || task.task_desc}</Text>
          </Paper>
        )}

        {/* File Attachment from manager */}
        {hasDisplayValue(task.file_url) && hasDisplayValue(task.file_name) && (
          <Box
            p="sm"
            style={{
              background: "#f3f0ff",
              borderRadius: 8,
              border: "1px solid #c5b3f5",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <IconUpload size={18} color="#7c3aed" />
            <Anchor
              href={task.file_url}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              c="violet.8"
              fw={500}
              style={{ flex: 1 }}
              lineClamp={1}
            >
              {task.file_name}
            </Anchor>
          </Box>
        )}

        {/* Manager feedback banner for rejected tasks */}
        {completedDetails?.is_rejected &&
          completedDetails?.manager_feedback && (
            <Box
              p="sm"
              style={{
                background: "#fef2f2",
                border: "1px solid #fca5a5",
                borderRadius: 8,
              }}
            >
              <Text size="xs" fw={700} c="red" mb={4}>
                Manager Feedback
              </Text>
              <Text size="sm" c="dark">
                {completedDetails.manager_feedback}
              </Text>
            </Box>
          )}

        {/* Completion Info */}
        {isCompleted && (
          <Paper p="md" radius="sm" withBorder bg="green.0">
            <Stack gap="md">
              {completedDetails.completed_at && (
                <Group gap="xs">
                  <IconCheck size={16} color="var(--mantine-color-green-6)" />
                  <Text size="sm" c="green" fw={600}>
                    Completed {formatDate(completedDetails.completed_at)}
                  </Text>
                </Group>
              )}

              {completedDetails.text_response && (
                <Box>
                  <Text size="sm" fw={600} mb="xs">
                    Your Response:
                  </Text>
                  <Paper p="sm" radius="sm" bg="white" withBorder>
                    <Text size="sm">{completedDetails.text_response}</Text>
                  </Paper>
                </Box>
              )}
            </Stack>
          </Paper>
        )}

        {/* Show results screen for non-quiz tasks */}
        {taskResult && <TaskResultsScreen />}

        {/* Action Area for Incomplete Tasks */}
        {!isCompleted && !taskResult && (
          <>
            {task.task_type === "USER_APPROVE" && (
              <Stack gap="md">
                {requiresImage && <ImageUploadSection />}
                {requiresAdminApproval && (
                  <Paper p="sm" radius="sm" withBorder bg="orange.0">
                    <Group gap="xs">
                      <IconShieldCheck
                        size={16}
                        color="var(--mantine-color-orange-6)"
                      />
                      <Text size="sm" c="orange" fw={500}>
                        After completing, your manager will need to approve this
                        task.
                      </Text>
                    </Group>
                  </Paper>
                )}
                <Button
                  fullWidth
                  size="lg"
                  loading={isSubmitting}
                  disabled={requiresImage && !imageFile}
                  onClick={handleComplete}
                  leftSection={<IconCheck size={20} />}
                  style={{ background: "#ff6b35", border: "none" }}
                >
                  {requiresImage && !imageFile
                    ? "Upload a photo to continue"
                    : "Mark as Completed"}
                </Button>
              </Stack>
            )}

            {task.task_type === "TEXT_RESPONSE" && (
              <Stack gap="md">
                <Textarea
                  placeholder="Enter your response..."
                  value={textResponse}
                  onChange={e => setTextResponse(e.target.value)}
                  minRows={4}
                  autosize
                  label="Your Response"
                />
                <ImageUploadSection />
                {requiresAdminApproval && (
                  <Paper p="sm" radius="sm" withBorder bg="orange.0">
                    <Group gap="xs">
                      <IconShieldCheck
                        size={16}
                        color="var(--mantine-color-orange-6)"
                      />
                      <Text size="sm" c="orange" fw={500}>
                        Your response will be reviewed by your manager before
                        this task is marked complete.
                      </Text>
                    </Group>
                  </Paper>
                )}
                <Button
                  fullWidth
                  size="lg"
                  loading={isSubmitting}
                  disabled={
                    !textResponse.trim() || (requiresImage && !imageFile)
                  }
                  onClick={handleSubmitResponse}
                  leftSection={<IconCheck size={20} />}
                  style={{ background: "#ff6b35", border: "none" }}
                >
                  Submit Response
                </Button>
              </Stack>
            )}

            {task.task_type === "VIDEO" && task.training_video && (
              <Stack gap="md">
                {hasDisplayValue(task.training_video.video_url) && (
                  <Box
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "#000",
                    }}
                  >
                    <video
                      ref={videoRef}
                      controls
                      controlsList="nodownload"
                      style={{
                        width: "100%",
                        maxHeight: "400px",
                        display: "block",
                      }}
                      src={task.training_video.video_url}
                      onTimeUpdate={e => {
                        const currentTime = e.target.currentTime;
                        // Track the furthest point watched
                        if (currentTime > maxWatchedTime) {
                          setMaxWatchedTime(currentTime);
                        }
                      }}
                      onSeeking={e => {
                        const currentTime = e.target.currentTime;
                        // Prevent seeking beyond the furthest watched point
                        if (currentTime > maxWatchedTime + 0.5) {
                          e.target.currentTime = maxWatchedTime;
                        }
                      }}
                      onEnded={async () => {
                        // Auto-complete when video ends
                        await handleComplete();
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </Box>
                )}
                <Paper p="sm" radius="sm" withBorder bg="blue.0">
                  <Text size="sm" c="blue" fw={500} ta="center">
                    Watch the video to completion to mark this task as done
                  </Text>
                </Paper>
              </Stack>
            )}

            {task.task_type === "PITCH_REVIEW" && (
              <Stack gap="md">
                <Paper p="md" radius="sm" withBorder bg="pink.0">
                  <Group gap="xs" mb="sm">
                    <IconMicrophone
                      size={16}
                      color="var(--mantine-color-pink-6)"
                    />
                    <Text size="sm" c="pink" fw={600}>
                      Pitch Review Required
                    </Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Upload a pitch video for your manager to review. This task
                    will be marked complete after submission and requires
                    manager approval.
                  </Text>
                </Paper>

                {selectedPitchVideo && (
                  <Paper p="md" radius="sm" withBorder>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <IconVideo
                          size={20}
                          color="var(--mantine-color-pink-6)"
                        />
                        <div>
                          <Text size="sm" fw={600}>
                            {selectedPitchVideo.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {(selectedPitchVideo.size / (1024 * 1024)).toFixed(
                              2
                            )}{" "}
                            MB
                          </Text>
                        </div>
                      </Group>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          setSelectedPitchVideo(null);
                          if (pitchVideoInputRef.current)
                            pitchVideoInputRef.current.value = "";
                        }}
                      >
                        Remove
                      </Button>
                    </Group>
                  </Paper>
                )}

                <Group gap="sm">
                  <Button
                    flex={1}
                    size="lg"
                    variant="outline"
                    onClick={() => pitchVideoInputRef.current?.click()}
                    leftSection={<IconUpload size={20} />}
                    color="pink"
                    disabled={uploadingPitch}
                  >
                    {selectedPitchVideo ? "Change Video" : "Select Video"}
                  </Button>
                  <Button
                    flex={1}
                    size="lg"
                    onClick={() => setShowPitchModal(true)}
                    leftSection={<IconMicrophone size={20} />}
                    color="pink"
                    disabled={uploadingPitch}
                  >
                    Record Video
                  </Button>
                </Group>

                {selectedPitchVideo && (
                  <Button
                    fullWidth
                    size="lg"
                    loading={uploadingPitch}
                    onClick={async () => {
                      setUploadingPitch(true);
                      try {
                        // For PITCH_REVIEW, just complete the task without the file
                        // The actual pitch video upload happens through the pitch review system
                        await onComplete(null);
                        setTaskResult({
                          success: true,
                          message: "Pitch Submitted!",
                          subtitle:
                            "Your pitch has been submitted for manager review. This task will be marked complete once approved.",
                        });
                      } catch (err) {
                        setTaskResult({
                          success: false,
                          message: "Failed to Submit Pitch",
                          error:
                            err?.message ||
                            err?.response?.data?.message ||
                            "An error occurred. Please try again.",
                        });
                      } finally {
                        setUploadingPitch(false);
                      }
                    }}
                    leftSection={<IconCheck size={20} />}
                    color="pink"
                  >
                    Submit Pitch
                  </Button>
                )}

                <input
                  ref={pitchVideoInputRef}
                  type="file"
                  accept="video/*"
                  style={{ display: "none" }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedPitchVideo(file);
                    }
                  }}
                />
              </Stack>
            )}

            {task.task_type === "UPLOAD" && (
              <Stack gap="md">
                <Paper p="md" radius="sm" withBorder bg="violet.0">
                  <Group gap="xs" mb="sm">
                    <IconUpload
                      size={16}
                      color="var(--mantine-color-violet-6)"
                    />
                    <Text size="sm" c="violet" fw={600}>
                      File Upload Required
                    </Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Upload a file to complete this task.
                    {requiresAdminApproval &&
                      " Your manager will review your submission."}
                  </Text>
                </Paper>

                {uploadedFile ? (
                  <Paper p="md" radius="sm" withBorder>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <IconUpload
                          size={20}
                          color="var(--mantine-color-violet-6)"
                        />
                        <div>
                          <Text size="sm" fw={600}>
                            {uploadedFile.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                          </Text>
                        </div>
                      </Group>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          setUploadedFile(null);
                          if (uploadFileInputRef.current)
                            uploadFileInputRef.current.value = "";
                        }}
                      >
                        Remove
                      </Button>
                    </Group>
                  </Paper>
                ) : (
                  <button
                    onClick={() => uploadFileInputRef.current?.click()}
                    style={{
                      width: "100%",
                      padding: "40px 20px",
                      border: "2px dashed #d1d5db",
                      borderRadius: 8,
                      background: "#f9fafb",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 12,
                      color: "#6b7280",
                    }}
                  >
                    <IconUpload size={32} stroke={1.5} />
                    <Text size="md" fw={600}>
                      Click to upload a file
                    </Text>
                    <Text size="sm" c="dimmed">
                      Any file type accepted
                    </Text>
                  </button>
                )}

                {uploadedFile && (
                  <Button
                    fullWidth
                    size="lg"
                    loading={isSubmitting}
                    onClick={async () => {
                      try {
                        await onComplete(uploadedFile);
                        setTaskResult({
                          success: true,
                          message: "File Uploaded!",
                          subtitle: requiresAdminApproval
                            ? "Your manager will review your submission"
                            : "Your file has been uploaded successfully",
                        });
                      } catch (err) {
                        setTaskResult({
                          success: false,
                          message: "Failed to Upload File",
                          error:
                            err?.message ||
                            err?.response?.data?.message ||
                            "An error occurred. Please try again.",
                        });
                      }
                    }}
                    leftSection={<IconCheck size={20} />}
                    style={{ background: "#ff6b35", border: "none" }}
                  >
                    Submit File
                  </Button>
                )}

                <input
                  ref={uploadFileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setUploadedFile(file);
                    }
                  }}
                />
              </Stack>
            )}

            {task.task_type === "ADMIN_APPROVE" && (
              <Paper p="md" radius="sm" withBorder>
                <Text size="sm" c="dimmed" fs="italic" ta="center">
                  This task requires manager approval. Complete any required
                  actions and wait for your manager to review.
                </Text>
              </Paper>
            )}

            {/* Multi-question quiz carousel */}
            {task.task_type === "QUIZ" && isMultiQuiz && <MultiQuizCarousel />}

            {/* Legacy single-question quiz */}
            {task.task_type === "QUIZ" &&
              !isMultiQuiz &&
              task.quiz_question && (
                <Stack gap="md">
                  <Paper p="md" radius="sm" withBorder>
                    <Text size="sm" fw={600} mb="xs" c="dimmed">
                      Question
                    </Text>
                    <Text size="sm" fw={500}>
                      {task.quiz_question}
                    </Text>
                  </Paper>

                  {Array.isArray(task.quiz_answers) &&
                    task.quiz_answers.length > 0 && (
                      <Stack gap="xs">
                        <Text size="sm" fw={600}>
                          Select your answer:
                        </Text>
                        {task.quiz_answers.map((answer, idx) => {
                          const isSelected = selectedAnswer === idx;
                          return (
                            <UnstyledButton
                              key={idx}
                              onClick={() => {
                                setSelectedAnswer(idx);
                                setQuizError(null);
                              }}
                              style={{
                                width: "100%",
                                padding: "12px 16px",
                                borderRadius: 8,
                                border: isSelected
                                  ? "2px solid var(--mantine-color-teal-6)"
                                  : "2px solid #e5e7eb",
                                background: isSelected
                                  ? "var(--mantine-color-teal-0)"
                                  : "#fff",
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <Box
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  border: isSelected
                                    ? "2px solid var(--mantine-color-teal-6)"
                                    : "2px solid #d1d5db",
                                  background: isSelected
                                    ? "var(--mantine-color-teal-6)"
                                    : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {isSelected && (
                                  <IconCheck size={14} color="white" />
                                )}
                              </Box>
                              <Text
                                size="sm"
                                fw={isSelected ? 600 : 400}
                                c={isSelected ? "teal" : "dark"}
                              >
                                {answer}
                              </Text>
                            </UnstyledButton>
                          );
                        })}
                      </Stack>
                    )}

                  {(quizError || externalQuizError) && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color="orange"
                      variant="light"
                    >
                      {quizError || externalQuizError}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    size="lg"
                    loading={isSubmitting}
                    disabled={selectedAnswer === null}
                    onClick={handleQuizSubmit}
                    leftSection={<IconCheck size={20} />}
                    color="teal"
                  >
                    {selectedAnswer === null
                      ? "Select an answer to continue"
                      : "Submit Answer"}
                  </Button>
                </Stack>
              )}
          </>
        )}
      </Stack>

      {/* Pitch Review Modal */}
      {showPitchModal && (
        <RecordPitchModal
          opened={showPitchModal}
          onClose={() => setShowPitchModal(false)}
          onSubmit={async result => {
            if (result?.success) {
              // Mark task as complete after successful pitch submission
              try {
                await onComplete(null);
                setTaskResult({
                  success: true,
                  message: "Pitch Submitted!",
                  subtitle:
                    "Your pitch has been submitted for manager review. This task will be marked complete once approved.",
                });
                setShowPitchModal(false);
              } catch (err) {
                setTaskResult({
                  success: false,
                  message: "Failed to Complete Task",
                  error:
                    err?.message ||
                    err?.response?.data?.message ||
                    "An error occurred. Please try again.",
                });
                setShowPitchModal(false);
              }
            }
          }}
          videoTitle={task.title || task.task_name || "Pitch Review"}
          videoId={null}
          courseId={null}
          initialChannelId={pitchChannelId}
          onSubmitSuccess={() => {}}
        />
      )}
    </Modal>
  );
}

export default ProgramTaskModal;
